import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import {
  Scale, ArrowRight, Flame, Thermometer, DoorOpen, Paintbrush,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Comparateurs travaux 2026 — Quelle solution choisir ? | AfficheServices' },
  { name: 'description', content: 'Comparateurs objectifs pour choisir entre deux solutions : chaudière vs PAC, double vs triple vitrage, laine de verre vs laine de roche. Prix, aides, performance.' },
  { name: 'robots', content: 'index, follow' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/comparaison' },
]

// Catalogue of all product/work comparisons — mirrors what's in
// routes/comparatif.$slug.tsx. Kept as a separate list here so this hub
// page can link to them with icons, categories, and descriptions
// without pulling the entire comparison data (which is route-scoped).
const CATEGORIES = [
  {
    title: 'Chauffage & énergie',
    color: 'from-orange-500 to-red-500',
    icon: Flame,
    items: [
      {
        slug: 'chaudiere-gaz-vs-pompe-a-chaleur',
        a: 'Chaudière gaz',
        b: 'Pompe à chaleur air-eau',
        desc: 'Coût total, aides 2026, performance grand froid, impact écologique.',
        popular: true,
      },
      {
        slug: 'chaudiere-granules-vs-pompe-a-chaleur',
        a: 'Chaudière à granulés',
        b: 'Pompe à chaleur',
        desc: 'Deux options vertes : laquelle est la plus rentable ?',
      },
      {
        slug: 'radiateur-electrique-vs-plancher-chauffant',
        a: 'Radiateur électrique',
        b: 'Plancher chauffant',
        desc: 'Confort, consommation, coût d\'installation.',
      },
    ],
  },
  {
    title: 'Isolation',
    color: 'from-emerald-500 to-teal-500',
    icon: Thermometer,
    items: [
      {
        slug: 'ite-vs-iti-isolation',
        a: 'ITE (extérieur)',
        b: 'ITI (intérieur)',
        desc: 'Performance thermique, coût, impact sur la surface habitable.',
        popular: true,
      },
      {
        slug: 'laine-de-verre-vs-laine-de-roche',
        a: 'Laine de verre',
        b: 'Laine de roche',
        desc: 'Prix, performance thermique, résistance au feu, acoustique.',
      },
    ],
  },
  {
    title: 'Menuiseries',
    color: 'from-blue-500 to-indigo-500',
    icon: DoorOpen,
    items: [
      {
        slug: 'simple-vitrage-vs-double-vitrage',
        a: 'Simple vitrage',
        b: 'Double vitrage',
        desc: 'Déperditions, économies, confort acoustique — les chiffres réels.',
      },
    ],
  },
  {
    title: 'Revêtements',
    color: 'from-amber-500 to-yellow-500',
    icon: Paintbrush,
    items: [
      {
        slug: 'peinture-acrylique-vs-glycero',
        a: 'Peinture acrylique',
        b: 'Peinture glycéro',
        desc: 'Aspect, durabilité, impact santé (COV), prix.',
      },
      {
        slug: 'parquet-massif-vs-stratifie',
        a: 'Parquet massif',
        b: 'Stratifié',
        desc: 'Durabilité, rendu, entretien, coût sur 20 ans.',
      },
    ],
  },
]

export default function ComparaisonHub() {
  const totalItems = CATEGORIES.reduce((sum, c) => sum + c.items.length, 0)

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Comparateurs', url: 'https://www.afficheservices.fr/comparaison' },
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-indigo-200">
            <Scale className="h-3.5 w-3.5" /> {totalItems} comparatifs objectifs
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl">Comparateurs travaux 2026</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-indigo-200">
            Vous hésitez entre deux solutions ? Nos comparatifs objectifs analysent prix, performance,
            aides disponibles et impact écologique pour vous aider à décider.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        {CATEGORIES.map((cat) => (
          <section key={cat.title} className="mb-14">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${cat.color} text-white shadow-sm`}>
                <cat.icon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{cat.title}</h2>
                <p className="text-sm text-slate-500">{cat.items.length} comparatif{cat.items.length > 1 ? 's' : ''}</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {cat.items.map((item) => (
                <Link
                  key={item.slug}
                  to={`/comparatif/${item.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-white p-5 shadow-xs transition-all hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5"
                >
                  {item.popular && (
                    <span className="absolute right-3 top-3 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      Populaire
                    </span>
                  )}
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <span className="truncate">{item.a}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">VS</span>
                    <span className="truncate">{item.b}</span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">{item.desc}</p>
                  <span className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary group-hover:underline">
                    Comparer <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {/* Methodology */}
        <section className="rounded-2xl border border-border bg-slate-50 p-8">
          <h2 className="text-lg font-bold text-slate-900">Notre méthodologie</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3 text-sm">
            <div>
              <p className="font-semibold text-slate-900">Données vérifiées</p>
              <p className="mt-1 text-slate-500 text-xs">Prix, performances et aides issus de sources officielles (ADEME, ANAH, INSEE).</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">Sans parti pris</p>
              <p className="mt-1 text-slate-500 text-xs">Pas d'affiliation avec les fabricants. Verdict basé sur les chiffres, pas sur la marge.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">Mise à jour annuelle</p>
              <p className="mt-1 text-slate-500 text-xs">Barèmes d'aides, prix moyens et normes révisés chaque année en janvier.</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 text-center">
          <h2 className="text-xl font-bold text-slate-900">Besoin d'un devis adapté ?</h2>
          <p className="mt-2 text-sm text-slate-500">Recevez des devis gratuits d'artisans vérifiés</p>
          <Link to="/demande-devis" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
